import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserUpdate } from '@/hooks';
import { Input } from '@/components/input';

import styles from '../Settings.module.css';

interface NotificationsSectionProps {
  onUpdate: (user: Api.UserObject) => void;
  user: Api.UserObject;
}

export function NotificationsSection({ onUpdate, user }: NotificationsSectionProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [wantEmailNotifications, setWantEmailNotifications] = useState<boolean>(
    user.want_email_notifications ?? true
  );
  const pendingValueRef = useRef<boolean | null>(null);

  function updateUser(res: Api.UserObject | ResponseObject) {
    if (res && (res as Api.UserObject).user_id) {
      onUpdate(res as Api.UserObject);
      setIsLoading(false);
      pendingValueRef.current = null;
    }
  }

  const { mutate: updateUserNotifications } = useUserUpdate({
    want_email_notifications: pendingValueRef.current ?? wantEmailNotifications,
    onSuccess: updateUser
  });

  useEffect(() => {
    setWantEmailNotifications(user.want_email_notifications ?? true);
  }, [user.want_email_notifications]);

  const handleToggleNotifications = () => {
    setIsLoading(true);
    // Toggle the current value: if currently true, set to false; if currently false, set to true
    const newValue = !wantEmailNotifications;
    setWantEmailNotifications(newValue);
    // Store the new value in ref so the hook can access it immediately
    pendingValueRef.current = newValue;
    // Call the mutation with the new value
    updateUserNotifications();
  };

  return (
    <div className={`${styles.section} ${styles.lastSection}`}>
      <div className={styles.sectionInfo}>
        <div className={styles.sectionInfoTitle}>{t('account.notificationsSection.title')}</div>
        <div className={styles.sectionInfoDescription}>
          {t('account.notificationsSection.description')}
        </div>
      </div>
      <div className={styles.sectionBody}>
        <div className={styles.sectionTable}>
          <div className={styles.row}>
            <div className={styles.grow}>
              <div className={`${styles.sectionBodyItem} ${styles.notificationsCheckbox}`}>
                <Input
                  type="checkbox"
                  name="allow_email_notifications"
                  checked={wantEmailNotifications}
                  onChange={handleToggleNotifications}
                  disabled={isLoading}
                  label={t('account.notificationsSection.allowEmailNotifications')}
                />
              </div>
            </div>
            <div className={styles.fitContent} />
          </div>
        </div>
      </div>
    </div>
  );
}
